// Browser auth client — local SQLite-backed API with Microsoft Entra

import { AuthFields, AuthProviders } from '@/_auth/interfaces/auth.interfaces';
import { AuthError, AuthSession, MfaAalResponse, MfaChallengeResponse, MfaEnrollResponse, MfaFactorsResponse } from '@/_auth/lib/auth.types';

type AuthChangeCallback = (event: string) => void;

const authListeners = new Set<AuthChangeCallback>();

const emitAuthChange = (event: string) => {
  authListeners.forEach((listener) => listener(event));
};

const parseJson = async <T>(response: Response): Promise<T> => response.json();

const authFetch = async (path: string, init?: RequestInit) => {
  return fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    credentials: 'include',
  });
};

const mfaApi = {
  listFactors: async (): Promise<{ data: MfaFactorsResponse | null; error: AuthError | null }> => {
    const response = await authFetch('/api/auth/mfa/factors');
    const json = await parseJson<{ data: MfaFactorsResponse; error: AuthError | null }>(response);

    if (!response.ok) {
      return { data: null, error: json.error ?? { message: 'Failed to list factors' } };
    }

    return { data: json.data, error: null };
  },

  enroll: async (params: { factorType: string; friendlyName?: string; issuer?: string }): Promise<{ data: MfaEnrollResponse | null; error: AuthError | null }> => {
    const response = await authFetch('/api/auth/mfa/enroll', {
      method: 'POST',
      body: JSON.stringify({
        [AuthFields.FactorType]: params.factorType,
        friendlyName: params.friendlyName,
      }),
    });
    const json = await parseJson<{ data: MfaEnrollResponse; error: AuthError | null }>(response);

    if (!response.ok) {
      return { data: null, error: json.error ?? { message: 'Enrollment failed' } };
    }

    return { data: json.data, error: null };
  },

  challenge: async (params: { factorId: string }): Promise<{ data: MfaChallengeResponse | null; error: AuthError | null }> => {
    const response = await authFetch('/api/auth/mfa/challenge', {
      method: 'POST',
      body: JSON.stringify({ factorId: params.factorId }),
    });
    const json = await parseJson<{ data: MfaChallengeResponse; error: AuthError | null }>(response);

    if (!response.ok) {
      return { data: null, error: json.error ?? { message: 'Challenge failed' } };
    }

    return { data: json.data, error: null };
  },

  verify: async (params: { factorId: string; challengeId: string; code: string }): Promise<{ data: { verified: boolean } | null; error: AuthError | null }> => {
    const response = await authFetch('/api/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    const json = await parseJson<{ data: { verified: boolean }; error: AuthError | null }>(response);

    if (!response.ok) {
      return { data: null, error: json.error ?? { message: 'Verification failed' } };
    }

    return { data: json.data, error: null };
  },

  unenroll: async (params: { factorId: string }): Promise<{ data: { unenrolled: boolean } | null; error: AuthError | null }> => {
    const response = await authFetch('/api/auth/mfa/unenroll', {
      method: 'POST',
      body: JSON.stringify({ factorId: params.factorId }),
    });
    const json = await parseJson<{ data: { unenrolled: boolean }; error: AuthError | null }>(response);

    if (!response.ok) {
      return { data: null, error: json.error ?? { message: 'Unenroll failed' } };
    }

    return { data: json.data, error: null };
  },

  getAuthenticatorAssuranceLevel: async (): Promise<{ data: MfaAalResponse | null; error: AuthError | null }> => {
    const response = await authFetch('/api/auth/mfa/aal');
    const json = await parseJson<{ data: MfaAalResponse; error: AuthError | null }>(response);
    return { data: json.data, error: json.error };
  },
};

export const authClient = {
  auth: {
    getSession: async (): Promise<{ data: { session: AuthSession | null }; error: AuthError | null }> => {
      const response = await authFetch('/api/auth/session');
      const json = await parseJson<{ session: AuthSession | null; error: AuthError | null }>(response);
      return { data: { session: json.session }, error: json.error };
    },

    getUser: async (): Promise<{ data: { user: AuthSession['user'] | null }; error: AuthError | null }> => {
      const { data, error } = await authClient.auth.getSession();
      return { data: { user: data.session?.user ?? null }, error };
    },

    signInWithMicrosoft: async (): Promise<{ error: AuthError | null }> => {
      window.location.href = '/api/auth/oauth/entra';
      return { error: null };
    },

    signInWithPassword: async (): Promise<{ data: { session: AuthSession | null }; error: AuthError | null }> => {
      return {
        data: { session: null },
        error: { message: 'Password sign-in is disabled. Use Microsoft Entra ID.' },
      };
    },

    signUp: async (): Promise<{ data: { session: AuthSession | null }; error: AuthError | null }> => {
      return {
        data: { session: null },
        error: { message: 'Self-service sign-up is disabled. Contact your administrator or sign in with Microsoft.' },
      };
    },

    signOut: async (): Promise<{ error: AuthError | null }> => {
      const response = await authFetch('/api/auth/signout', { method: 'POST' });
      const json = await parseJson<{ error: AuthError | null }>(response);
      emitAuthChange('SIGNED_OUT');
      return { error: json.error };
    },

    resetPasswordForEmail: async (): Promise<{ error: AuthError | null }> => {
      return {
        error: { message: 'Password reset is managed in Microsoft Entra ID.' },
      };
    },

    updateUser: async (params: { password?: string }): Promise<{ data: { user: AuthSession['user'] | null }; error: AuthError | null }> => {
      const response = await authFetch('/api/auth/user', {
        method: 'PUT',
        body: JSON.stringify({
          [AuthFields.Password]: params.password,
        }),
      });
      const json = await parseJson<{ user: AuthSession['user']; error: AuthError | null }>(response);

      if (!response.ok) {
        return { data: { user: null }, error: json.error ?? { message: 'Update failed' } };
      }

      emitAuthChange('USER_UPDATED');
      return { data: { user: json.user }, error: null };
    },

    refreshSession: async (): Promise<{ data: { session: AuthSession | null }; error: AuthError | null }> => {
      const response = await authFetch('/api/auth/session', { method: 'POST' });
      const json = await parseJson<{ session: AuthSession | null; error: AuthError | null }>(response);

      if (!response.ok) {
        return { data: { session: null }, error: json.error ?? { message: 'Refresh failed' } };
      }

      emitAuthChange('TOKEN_REFRESHED');
      return { data: { session: json.session }, error: null };
    },

    signInWithOAuth: async (params: { provider: AuthProviders | string }): Promise<{ error: AuthError | null }> => {
      if (params.provider === AuthProviders.Microsoft || params.provider === 'microsoft' || params.provider === 'entra') {
        return authClient.auth.signInWithMicrosoft();
      }

      return { error: { message: `Unsupported OAuth provider: ${params.provider}` } };
    },

    exchangeCodeForSession: async (): Promise<{ error: AuthError | null }> => {
      // Handled server-side in callback routes; client just refreshes session state
      emitAuthChange('SIGNED_IN');
      return { error: null };
    },

    onAuthStateChange: (callback: AuthChangeCallback) => {
      authListeners.add(callback);
      return {
        data: {
          subscription: {
            unsubscribe: () => authListeners.delete(callback),
          },
        },
      };
    },

    mfa: mfaApi,
  },
};

export function createClient() {
  return authClient;
}
